"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
function BookingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-foreground text-background border border-secondary/20 rounded-none flex flex-col items-center justify-center text-center min-h-[400px]">
        <h3 className="font-default text-3xl text-secondary mb-4">Inquiry Received</h3>
        <p className="text-background/80 font-sans max-w-md">
          {successMessage}
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="firstName" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-widest text-foreground/70">First Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane" className="bg-foreground text-background border-transparent focus-visible:ring-secondary focus-visible:border-secondary placeholder:text-background/30 rounded-none h-12 px-4" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="lastName" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-widest text-foreground/70">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" className="bg-foreground text-background border-transparent focus-visible:ring-secondary focus-visible:border-secondary placeholder:text-background/30 rounded-none h-12 px-4" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-widest text-foreground/70">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-foreground text-background border-transparent focus-visible:ring-secondary focus-visible:border-secondary placeholder:text-background/30 rounded-none h-12 px-4" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-widest text-foreground/70">Phone Number (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-foreground text-background border-transparent focus-visible:ring-secondary focus-visible:border-secondary placeholder:text-background/30 rounded-none h-12 px-4" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="dates" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-mono uppercase tracking-widest text-foreground/70">Preferred Dates</FormLabel>
            <FormControl>
              <Input placeholder="e.g., October 15 - October 22" className="bg-foreground text-background border-transparent focus-visible:ring-secondary focus-visible:border-secondary placeholder:text-background/30 rounded-none h-12 px-4" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please provide some details about your inquiry"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-mono uppercase tracking-widest text-foreground/70">Inquiry Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your ideal experience..." className="bg-foreground text-background border-transparent focus-visible:ring-secondary focus-visible:border-secondary placeholder:text-background/30 rounded-none min-h-[120px] p-4 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-none h-14 text-base tracking-widest uppercase font-mono bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : 'Submit Inquiry'}
      </Button>
    </Form>;
}
export default function BookYourLuxuryExperience() {
  return <section id="book-your-luxury-experience" className="bg-background py-24 md:py-32 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-0 items-center">
          
          {}
          <div className="lg:col-span-5 relative z-10">
            <AspectRatio ratio={3 / 4} className="bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/luxury_penthouse_view.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/luxury_penthouse_view.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/luxury_penthouse_view.png-wvc-sizes" alt="Luxury penthouse view" className="object-cover w-full h-full" loading="lazy" />
            </AspectRatio>
          </div>

          {}
          <div className="lg:col-span-7 relative z-20 flex flex-col justify-center">
            
            {}
            <div className="lg:-ml-24 xl:-ml-32 mb-12 lg:mb-16 relative">
              <h2 className="font-default text-secondary text-5xl md:text-6xl lg:text-7xl xl:text-[5rem] leading-[0.9] tracking-tight drop-shadow-sm">
                Book Your<br />
                <span className="italic">Luxury</span> Experience
              </h2>
            </div>

            {}
            <div className="lg:pl-12 xl:pl-24 w-full max-w-2xl">
              <FormProvider formId="luxury-experience-booking" sectionName="Book Your Luxury Experience" defaultValues={{
              firstName: "",
              lastName: "",
              email: "",
              phone: "",
              dates: "",
              message: ""
            }} submitText="Submit Inquiry" successMessage="Thank you for your inquiry. Our concierge team will contact you shortly to curate your experience.">
                <BookingForm />
              </FormProvider>
            </div>

          </div>
        </div>
      </div>
    </section>;
}